import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';

/**
 * Fetches and manages the current user's NIP-51 mute list (kind 10000).
 * Only manages public mute entries (p tags).
 */
export function useMuteList() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['nostr', 'mute-list', user?.pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      const [event] = await nostr.query(
        [{ kinds: [10000], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );
      return event?.tags.filter(([name]) => name === 'p').map(([, pubkey]) => pubkey) ?? [];
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const mutedPubkeys: string[] = query.data ?? [];

  function isMuted(pubkey: string) {
    return mutedPubkeys.includes(pubkey);
  }

  async function mute(pubkey: string) {
    if (!user) {
      toast({ title: 'Not logged in', description: 'You must be logged in to mute someone.', variant: 'destructive' });
      return;
    }
    if (isMuted(pubkey)) return;

    const [existing] = await nostr.query(
      [{ kinds: [10000], authors: [user.pubkey], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    const existingTags = existing?.tags ?? [];

    try {
      await publish({
        kind: 10000,
        content: existing?.content ?? '',
        tags: [...existingTags, ['p', pubkey]],
      });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'mute-list', user.pubkey] });
      toast({ title: 'Muted', description: 'This user has been muted.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Failed to mute user. Please try again.', variant: 'destructive' });
    }
  }

  async function unmute(pubkey: string) {
    if (!user) {
      toast({ title: 'Not logged in', description: 'You must be logged in to unmute someone.', variant: 'destructive' });
      return;
    }
    if (!isMuted(pubkey)) return;

    const [existing] = await nostr.query(
      [{ kinds: [10000], authors: [user.pubkey], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    const existingTags = existing?.tags ?? [];
    const filteredTags = existingTags.filter(([name, value]) => !(name === 'p' && value === pubkey));

    try {
      await publish({
        kind: 10000,
        content: existing?.content ?? '',
        tags: filteredTags,
      });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'mute-list', user.pubkey] });
      toast({ title: 'Unmuted', description: 'This user has been unmuted.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Failed to unmute user. Please try again.', variant: 'destructive' });
    }
  }

  return {
    mutedPubkeys,
    isMuted,
    mute,
    unmute,
    isLoading: query.isLoading,
  };
}
